'use client';

import { useState, useCallback, useMemo, useEffect } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  ConnectionMode,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { 
  AlertTriangle,
  Loader2,
  Filter,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  useTraceability,
  type TraceabilityNode,
} from '@/hooks/use-traceability';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface TraceabilityGraphProps {
  projectId: string;
  projectName?: string;
}

const NODE_COLORS: Record<string, string> = {
  goal: '#6366f1',
  persona: '#8b5cf6',
  useCase: '#ec4899',
  journey: '#f59e0b',
  baseSpec: '#10b981',
  contract: '#06b6d4',
  technicalSpec: '#3b82f6',
  story: '#ef4444',
  evidence: '#84cc16',
};

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: '#10b981',
  DRAFT: '#6b7280',
  IN_REVIEW: '#3b82f6',
  APPROVED: '#8b5cf6',
  ARCHIVED: '#9ca3af',
};

export function TraceabilityGraph({ projectId, projectName = 'Project' }: TraceabilityGraphProps) {
  const [selectedNodeType, setSelectedNodeType] = useState<string>('all');
  const [showGaps, setShowGaps] = useState(true);
  const [selectedNode, setSelectedNode] = useState<TraceabilityNode | null>(null);
  const [showNodeDetails, setShowNodeDetails] = useState(false);
  
  const { toast } = useToast();
  const { data: graphData, isLoading, isError } = useTraceability(projectId);

  // Convert traceability nodes to React Flow nodes
  const reactFlowNodes = useMemo(() => {
    if (!graphData) return [];

    // Filter nodes by type if filter is set
    const filteredNodes = selectedNodeType === 'all' 
      ? graphData.nodes 
      : graphData.nodes.filter(n => n.type === selectedNodeType);

    // Create a simple grid layout
    const nodesPerRow = Math.ceil(Math.sqrt(filteredNodes.length));
    const nodeSpacing = 200;

    return filteredNodes.map((node, index) => {
      const row = Math.floor(index / nodesPerRow);
      const col = index % nodesPerRow;
      const color = NODE_COLORS[node.type] || '#6b7280';
      const statusColor = STATUS_COLORS[node.status] || '#6b7280';
      
      // Check if this node has a gap
      const hasGap = showGaps && graphData.gaps.some(gap => {
        if (gap.sourceId && node.id.includes(gap.sourceId)) return true;
        return false;
      });
      const gapSeverity = hasGap 
        ? graphData.gaps.find(gap => gap.sourceId && node.id.includes(gap.sourceId))?.severity 
        : null;

      return {
        id: node.id,
        type: 'default',
        position: { 
          x: 100 + col * nodeSpacing, 
          y: 100 + row * nodeSpacing 
        },
        data: { 
          label: (
            <div className="flex flex-col items-center gap-1">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium shadow-md"
                style={{ backgroundColor: color }}
              >
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: statusColor }}
                />
              </div>
              <span className="text-xs font-medium text-gray-700 max-w-[100px] text-center truncate">
                {node.label}
              </span>
              <span className="text-xs text-gray-500 capitalize">{node.type}</span>
            </div>
          ),
          nodeData: node,
        },
        style: {
          border: hasGap 
            ? `3px solid ${gapSeverity === 'high' ? '#ef4444' : gapSeverity === 'medium' ? '#f59e0b' : '#eab308'}`
            : '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '8px',
          backgroundColor: 'white',
        },
      } as Node;
    });
  }, [graphData, selectedNodeType, showGaps]);

  // Convert traceability edges to React Flow edges
  const reactFlowEdges = useMemo(() => {
    if (!graphData) return [];

    return graphData.edges.map((edge) => ({
      id: `${edge.source}-${edge.target}`,
      source: edge.source,
      target: edge.target,
      type: 'smoothstep',
      animated: false,
      style: { stroke: '#94a3b8', strokeWidth: 2 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#94a3b8',
      },
    })) as Edge[];
  }, [graphData]);

  // Get unique node types from graph data - must be before any early returns
  const nodeTypes = useMemo(() => 
    Array.from(new Set(graphData?.nodes.map(n => n.type) || [])),
    [graphData]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(reactFlowNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(reactFlowEdges);

  // Update nodes and edges when data changes
  useEffect(() => {
    setNodes(reactFlowNodes);
    setEdges(reactFlowEdges);
  }, [reactFlowNodes, reactFlowEdges, setNodes, setEdges]);

  // Handle node click
  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    const nodeData = node.data.nodeData as TraceabilityNode;
    setSelectedNode(nodeData);
    setShowNodeDetails(true);
  }, []);

  // Handle pane click to deselect
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setShowNodeDetails(false);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError || !graphData) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <AlertTriangle className="w-16 h-16 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Traceability</h3>
        <p className="text-gray-600">Unable to load traceability graph data.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="border-b bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Traceability Graph</h1>
            <p className="text-sm text-gray-600 mt-1">End-to-end traceability visualization for {projectName}</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedNodeType} onValueChange={setSelectedNodeType}>
              <SelectTrigger className="w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {nodeTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={showGaps ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowGaps(!showGaps)}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              {showGaps ? 'Hide Gaps' : 'Show Gaps'}
            </Button>
          </div>
        </div>
      </div>

      {/* React Flow Graph */}
      <div className="flex-1 relative bg-gray-50">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          connectionMode={ConnectionMode.Loose}
          fitView
          attributionPosition="bottom-left"
        >
          <Background color="#e5e7eb" gap={16} />
          <Controls />
          <MiniMap 
            nodeColor={(node) => {
              const nodeData = node.data?.nodeData as TraceabilityNode | undefined;
              return nodeData ? (NODE_COLORS[nodeData.type] || '#6b7280') : '#6b7280';
            }}
            maskColor="rgba(0, 0, 0, 0.1)"
          />
        </ReactFlow>
      </div>

      {/* Node Details Dialog */}
      {selectedNode && (
        <Dialog open={showNodeDetails} onOpenChange={setShowNodeDetails}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: NODE_COLORS[selectedNode.type] || '#6b7280' }}
                />
                {selectedNode.label}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-700">Type:</span>
                <Badge className="ml-2 capitalize">{selectedNode.type}</Badge>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Status:</span>
                <Badge 
                  className="ml-2"
                  style={{ 
                    backgroundColor: STATUS_COLORS[selectedNode.status] || '#6b7280',
                    color: 'white'
                  }}
                >
                  {selectedNode.status}
                </Badge>
              </div>
              {selectedNode.metadata && Object.keys(selectedNode.metadata).length > 0 && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Metadata:</span>
                  <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-auto max-h-40">
                    {JSON.stringify(selectedNode.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Legend and Stats */}
      <div className="border-t bg-white px-6 py-4">
        <div className="grid grid-cols-2 gap-4">
          {/* Coverage Stats */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Coverage</h3>
            <div className="grid grid-cols-3 gap-2 text-xs">
              {graphData.coverage && Object.entries(graphData.coverage).map(([type, count]) => (
                <div key={type} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: NODE_COLORS[type] || '#6b7280' }}
                  />
                  <span className="text-gray-600 capitalize">{type}:</span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Gaps */}
          {graphData.gaps && graphData.gaps.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Gaps ({graphData.gaps.length})</h3>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {graphData.gaps.slice(0, 5).map((gap, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <Badge 
                      variant={gap.severity === 'high' ? 'destructive' : gap.severity === 'medium' ? 'default' : 'secondary'}
                    >
                      {gap.severity}
                    </Badge>
                    <span className="text-gray-600">{gap.message}</span>
                  </div>
                ))}
                {graphData.gaps.length > 5 && (
                  <span className="text-xs text-gray-500">+{graphData.gaps.length - 5} more</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
